import React, { useContext, useEffect } from "react";
import { LineChart } from '@mui/x-charts/LineChart';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from '../context/BusinessContext';
import {
  CHART_MARGINS,
  MOBILE_CHART_MARGINS,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme } from '@mui/material';
import { ChartStateWrapper } from './RatingChartUtils';

// Independent Cumulative Rating Chart component
const CumulativeRatingChart = () => {
  const { 
    view, // Keep for tooltip
    cumulativeChartData, 
    isCumulativeChartLoading, 
    cumulativeChartError,
    fetchCumulativeChartData, // Add fetchCumulativeChartData from context
    timeframe,
    selectedMonthRange,
    selectedSources
  } = useContext(ReviewsContext);
  
  const { currencySymbol, selectedBusinesses } = useContext(BusinessContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Add useEffect to fetch cumulative chart data when component mounts or dependencies change
  useEffect(() => {
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      // Add a small delay to prevent simultaneous API calls with other components
      const timer = setTimeout(() => {
        fetchCumulativeChartData();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [selectedBusinesses, timeframe, selectedMonthRange, selectedSources, fetchCumulativeChartData]);

  // Determine noData state based on new context data structure
  const noData = !cumulativeChartData || !cumulativeChartData.series_data || cumulativeChartData.series_data.length === 0;
  const numSelected = selectedBusinesses?.length || 0;



  // Use new loading/error states from context
  return (
    <ChartStateWrapper loading={isCumulativeChartLoading} error={cumulativeChartError} noData={noData}>
      <LineChart
        colors={['#1976d2']} 
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'hidden',
          // Fix mobile scroll issue - allow pan-y (vertical scrolling) while preserving chart interactions
          touchAction: 'pan-y pinch-zoom',
          '& svg, & path, & g': {
            maxHeight: '350px !important'
          },
          '.MuiChartsAxis-label': {
            fill: '#546E7A',
            fontWeight: 500,
            fontSize: isSmallScreen ? 10 : 12
          },
          '.MuiChartsAxis-tickLabel': {
            fill: '#546E7A',
            fontSize: isSmallScreen ? 9 : 11
          },
          '.MuiLineElement-series-ratingTrend': {
            strokeWidth: 1.5
          },
          '.MuiAreaElement-series-ratingTrend': {
            fillOpacity: 0.05,
            display: 'block !important',
            clipPath: 'inset(0 0 0 0)'
          },
          '& .MuiChart-area': {
            overflow: 'hidden'
          },
          '& .MuiChartsAxis-root, & .MuiChartsLegend-root': {
            overflow: 'hidden'
          },
          // Additional mobile-specific touch handling
          '& svg': {
            touchAction: 'pan-y pinch-zoom',
            pointerEvents: 'auto'
          }
        }}
        xAxis={[
          {
            // Data from context: cumulativeChartData.series_data.map(p => new Date(p.x)) 
            // (ensure p.x is valid for Date constructor, backend sends datetime string)
            data: cumulativeChartData && cumulativeChartData.series_data ? cumulativeChartData.series_data.map(p => new Date(p.x)) : [],
            scaleType: 'time',
            tickLabelStyle: {
              angle: 45, 
              textAnchor: 'start', 
              fontSize: isSmallScreen ? 10 : 12, 
            },
            tickLabelInterval: () => true,
            valueFormatter: (value, context) => {
              if (context.location === 'tooltip') {
                return new Date(value).toLocaleDateString(); 
              }
              // For axis ticks, MUI charts will format time scale automatically, 
              // but if specific formatting is needed, it can be done here.
              // return new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
            },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => `${value}`,
            tickLabelStyle: {
              fontSize: isSmallScreen ? 9 : 11,
            },
            // Use y_min and y_max from context data
            min: cumulativeChartData ? cumulativeChartData.y_min : 0,
            max: cumulativeChartData ? cumulativeChartData.y_max : 5,
            tickNumber: 3
          },
        ]}
        series={[
          {
            id: 'ratingTrend',
            // Data from context: cumulativeChartData.series_data.map(p => p.y)
            data: cumulativeChartData && cumulativeChartData.series_data ? cumulativeChartData.series_data.map(p => p.y) : [],
            label: 'Rating',
            showMark: false,
            area: true, 
            lineStyle: {
              strokeWidth: 2
            },
            areaStyle: {
              fillOpacity: 0.05,
              display: 'block'
            }
          },
        ]}
        slotProps={{
          legend: { hidden: true },
          tooltip: {
            content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} />
          },
          svg: { 
            style: {
              maxHeight: '350px',
              overflow: 'hidden'
            },
            // Adjust viewBox based on actual data length if necessary, or keep fixed
            viewBox: `0 0 ${cumulativeChartData && cumulativeChartData.series_data && cumulativeChartData.series_data.length > 0 ? cumulativeChartData.series_data.length * 10 : 100} 100`
          }
        }}
        margin={isSmallScreen ? 
          {...MOBILE_CHART_MARGINS, left: 40, right:40} : 
          CHART_MARGINS
        }
      />
    </ChartStateWrapper>
  );
};

export default CumulativeRatingChart; 